import { useEffect, useState, useRef } from "react";
import { Plus, Search, Pencil, Trash2, Copy, Upload, Star, X, ImageIcon } from "lucide-react";
import { api, formatPrice, img } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CATEGORIES = ["Apparel", "Home Decor", "Christian Products", "Gifts", "Lifestyle", "Print-on-Demand", "Seasonal"];
const EMPTY = { name: "", description: "", category: "Apparel", regular_price: "", sale_price: "",
  on_sale: false, sku: "", stock: 0, images: [], tags: "", is_new: false, is_best_seller: false, is_featured: false };

function ProductForm({ open, onClose, editingId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && editingId) {
      api.get(`/admin/products/${editingId}`).then((r) => {
        const d = r.data;
        setForm({ ...EMPTY, ...d, regular_price: d.regular_price ?? "", sale_price: d.sale_price ?? "",
          tags: (d.tags || []).join(", "), sku: d.sku || "" });
      });
    } else if (open) setForm(EMPTY);
  }, [open, editingId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Image uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addUrl = () => {
    const url = window.prompt("Paste an image URL");
    if (url) set("images", [...form.images, url.trim()]);
  };
  const removeImg = (idx) => set("images", form.images.filter((_, i) => i !== idx));
  const makeMain = (idx) => set("images", [form.images[idx], ...form.images.filter((_, i) => i !== idx)]);

  const save = async (e) => {
    e.preventDefault();
    if (form.on_sale && (!form.sale_price || Number(form.sale_price) >= Number(form.regular_price))) {
      toast.error("Sale price must be a positive value below the regular price."); return;
    }
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, category: form.category,
      regular_price: Number(form.regular_price), sale_price: form.sale_price === "" ? null : Number(form.sale_price),
      on_sale: form.on_sale, sku: form.sku || null, stock: Number(form.stock), images: form.images,
      tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      is_new: form.is_new, is_best_seller: form.is_best_seller, is_featured: form.is_featured,
    };
    try {
      if (editingId) await api.patch(`/admin/products/${editingId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editingId ? "Product updated" : "Product created");
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="product-form">
          <Input required placeholder="Product name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none" data-testid="pf-name" />
          <Textarea placeholder="Description" value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-none" data-testid="pf-description" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Category</label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="rounded-none mt-1" data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">SKU</label>
              <Input placeholder="e.g. GLT-APP-001" value={form.sku} onChange={(e) => set("sku", e.target.value)} className="rounded-none mt-1" data-testid="pf-sku" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Regular Price</label>
              <Input required type="number" step="0.01" min="0" value={form.regular_price} onChange={(e) => set("regular_price", e.target.value)} className="rounded-none mt-1" data-testid="pf-regular-price" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Sale Price</label>
              <Input type="number" step="0.01" min="0" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} className="rounded-none mt-1" data-testid="pf-sale-price" />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch checked={form.on_sale} onCheckedChange={(v) => set("on_sale", v)} data-testid="pf-on-sale" />
              <span className="text-sm">On Sale</span>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Stock Quantity</label>
            <Input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="rounded-none mt-1 w-40" data-testid="pf-stock" />
          </div>

          {/* Images */}
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Product Images</label>
            <div className="flex flex-wrap gap-3 mt-2">
              {form.images.map((src, i) => (
                <div key={src + i} className="relative w-24 h-24 border border-border group" data-testid={`pf-image-${i}`}>
                  <img src={img(src)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute top-0 left-0 bg-[#D4AF37] text-black text-[9px] px-1">MAIN</span>}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
                    {i !== 0 && <button type="button" onClick={() => makeMain(i)} title="Set as main" className="text-white" data-testid={`pf-setmain-${i}`}><Star className="h-4 w-4" /></button>}
                    <button type="button" onClick={() => removeImg(i)} title="Remove" className="text-white" data-testid={`pf-removeimg-${i}`}><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-24 h-24 border border-dashed border-border grid place-items-center text-muted-foreground hover:border-[#D4AF37]" data-testid="pf-upload-btn">
                {uploading ? <span className="text-xs">…</span> : <Upload className="h-5 w-5" />}
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="pf-file-input" />
            <button type="button" onClick={addUrl} className="text-xs text-[#D4AF37] underline mt-2">or paste an image URL</button>
          </div>

          <Input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => set("tags", e.target.value)} className="rounded-none" data-testid="pf-tags" />
          <div className="flex flex-wrap gap-6">
            {[["is_new", "New"], ["is_best_seller", "Best Seller"], ["is_featured", "Featured"]].map(([k, label]) => (
              <div key={k} className="flex items-center gap-2"><Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`pf-${k}`} /><span className="text-sm">{label}</span></div>
            ))}
          </div>
          <Button disabled={saving} className="w-full rounded-none h-11 bg-[#D4AF37] text-black font-semibold hover:brightness-110" data-testid="pf-save">
            {saving ? "Saving…" : editingId ? "Update Product" : "Create Product"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    const params = new URLSearchParams({ limit: "100" });
    if (q) params.set("search", q);
    if (cat !== "all") params.set("category", cat);
    api.get(`/products?${params}`).then((r) => setProducts(r.data.items));
  };
  useEffect(() => { load(); }, [q, cat]);

  const openAdd = () => { setEditingId(null); setFormOpen(true); };
  const openEdit = (id) => { setEditingId(id); setFormOpen(true); };
  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };
  const dup = async (id) => { await api.post(`/admin/products/${id}/duplicate`); toast.success("Duplicated"); load(); };

  return (
    <div data-testid="admin-products-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl">Products <span className="text-muted-foreground text-base">({products.length})</span></h2>
        <Button onClick={openAdd} className="rounded-none bg-[#D4AF37] text-black" data-testid="admin-add-product"><Plus className="h-4 w-4 mr-1" />Add Product</Button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="rounded-none pl-9" data-testid="admin-product-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-52 rounded-none" data-testid="admin-product-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left">
            {["", "Product", "SKU", "Category", "Price", "Stock", "Flags", "Actions"].map((h, i) => <th key={i} className="p-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid={`admin-product-row-${p.slug}`}>
                <td className="p-2"><div className="w-12 h-12 bg-[#F5F1E8] dark:bg-[#141414]">{p.images?.[0] && <img src={img(p.images[0])} alt="" className="w-full h-full object-cover" />}</div></td>
                <td className="p-3 font-medium max-w-[220px] truncate">{p.name}</td>
                <td className="p-3 text-muted-foreground">{p.sku || "—"}</td>
                <td className="p-3">{p.category}</td>
                <td className="p-3">{p.on_sale && p.compare_at_price ? (<span><span className="text-[#D4AF37]">{formatPrice(p.price)}</span> <span className="line-through text-muted-foreground text-xs">{formatPrice(p.compare_at_price)}</span></span>) : formatPrice(p.price)}</td>
                <td className="p-3"><span className={p.stock <= 0 ? "text-destructive" : p.stock <= 5 ? "text-amber-600" : ""}>{p.stock}</span></td>
                <td className="p-3"><div className="flex gap-1">{p.is_new && <span className="text-[9px] bg-black text-white px-1">NEW</span>}{p.on_sale && <span className="text-[9px] bg-[#D4AF37] text-black px-1">SALE</span>}{p.is_best_seller && <span className="text-[9px] border px-1">BEST</span>}{p.is_featured && <span className="text-[9px] border px-1">FEAT</span>}</div></td>
                <td className="p-3"><div className="flex gap-2">
                  <button onClick={() => openEdit(p.id)} className="hover:text-[#D4AF37]" title="Edit" data-testid={`admin-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => dup(p.id)} className="hover:text-[#D4AF37]" title="Duplicate" data-testid={`admin-dup-${p.slug}`}><Copy className="h-4 w-4" /></button>
                  <button onClick={() => del(p.id)} className="hover:text-destructive" title="Delete" data-testid={`admin-del-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                </div></td>
              </tr>
            ))}
            {products.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground"><ImageIcon className="h-8 w-8 mx-auto mb-2" />No products found.</td></tr>}
          </tbody>
        </table>
      </div>
      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} editingId={editingId} onSaved={load} />
    </div>
  );
}

export function AdminInventory() {
  const [data, setData] = useState({ items: [], low_stock_threshold: 5 });
  const [edits, setEdits] = useState({});
  const load = () => api.get("/admin/inventory").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    const v = edits[id];
    if (v === undefined) return;
    if (Number(v) < 0) { toast.error("Stock cannot be negative"); return; }
    await api.patch(`/admin/inventory/${id}`, { stock: Number(v) });
    toast.success("Stock updated"); setEdits((e) => { const n = { ...e }; delete n[id]; return n; }); load();
  };

  const low = data.items.filter((i) => i.status === "low").length;
  const out = data.items.filter((i) => i.status === "out").length;

  return (
    <div data-testid="admin-inventory-section">
      <h2 className="font-display text-2xl mb-6">Inventory</h2>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-border p-4"><p className="font-display text-3xl">{data.items.length}</p><p className="text-sm text-muted-foreground">Total products</p></div>
        <div className="border border-border p-4"><p className="font-display text-3xl text-amber-600">{low}</p><p className="text-sm text-muted-foreground">Low stock (≤{data.low_stock_threshold})</p></div>
        <div className="border border-border p-4"><p className="font-display text-3xl text-destructive">{out}</p><p className="text-sm text-muted-foreground">Out of stock</p></div>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left">{["", "Product", "SKU", "Status", "Stock", ""].map((h, i) => <th key={i} className="p-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {data.items.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`inv-row-${it.id}`}>
                <td className="p-2"><div className="w-10 h-10 bg-[#F5F1E8] dark:bg-[#141414]">{it.image && <img src={img(it.image)} alt="" className="w-full h-full object-cover" />}</div></td>
                <td className="p-3 max-w-[220px] truncate">{it.name}</td>
                <td className="p-3 text-muted-foreground">{it.sku || "—"}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 ${it.status === "out" ? "bg-destructive text-white" : it.status === "low" ? "bg-amber-500 text-black" : "bg-secondary"}`}>{it.status === "out" ? "Out" : it.status === "low" ? "Low" : "OK"}</span></td>
                <td className="p-3"><Input type="number" min="0" value={edits[it.id] ?? it.stock} onChange={(e) => setEdits((ed) => ({ ...ed, [it.id]: e.target.value }))} className="rounded-none w-24 h-9" data-testid={`inv-input-${it.id}`} /></td>
                <td className="p-3">{edits[it.id] !== undefined && <Button onClick={() => save(it.id)} className="rounded-none h-9 bg-[#D4AF37] text-black" data-testid={`inv-save-${it.id}`}>Save</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
